import { useLanguage } from '../../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiLock, FiChevronLeft, FiCreditCard, FiMapPin, FiEdit2, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import orderService from '../../services/order.service';
import addressService from '../../services/address.service';
import AddressBook from '../../components/address/AddressBook';
import ShippingRateSelector from '../../components/shipping/ShippingRateSelector';
import taxService from '../../services/tax.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CheckoutPage = () => {
  const { t } = useLanguage();
  const { cart, itemCount, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressMode, setAddressMode] = useState('saved'); // 'saved' | 'manual'
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [shippingAddress, setShippingAddress] = useState({
    fullName: user ? `${user.firstName} ${user.lastName}` : '',
    street: '', apartment: '', city: '', state: '', zip: '', country: 'US', phone: user?.phone || '',
  });
  const [billingAddress, setBillingAddress] = useState(null);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [notes, setNotes] = useState('');
  const [shippingRate, setShippingRate] = useState(null);
  const [taxInfo, setTaxInfo] = useState({ totalTax: 0, taxRate: 0.08, taxName: 'Tax' });

  const tax = taxInfo.totalTax;
  const shipping = shippingRate?.rate || 0;
  const total = subtotal + tax + shipping;

  useEffect(() => {
    if (!cart.items || cart.items.length === 0) navigate('/cart');
  }, [cart, navigate]);

  // Fetch dynamic tax when address changes
  useEffect(() => {
    const fetchTax = async () => {
      if (!cart.items?.length) return;
      try {
        const items = cart.items.map(i => ({ productId: i.productId?._id || i.productId, quantity: i.quantity }));
        const res = await taxService.calculate(items, shippingAddress.country, shippingAddress.state);
        setTaxInfo(res.data.data);
      } catch {
        setTaxInfo({ totalTax: subtotal * 0.08, taxRate: 0.08, taxName: 'Tax (est.)' });
      }
    };
    fetchTax();
  }, [cart.items, shippingAddress.country, shippingAddress.state, subtotal]);

  // Fetch saved addresses on mount
  useEffect(() => {
    const fetchAddrs = async () => {
      try {
        const res = await addressService.getAll();
        const addrs = res.data.data || [];
        setSavedAddresses(addrs);
        if (addrs.length > 0) {
          setAddressMode('saved');
          const def = addrs.find(a => a.isDefault) || addrs[0];
          setSelectedAddressId(def._id);
          applyAddress(def);
        } else {
          setAddressMode('manual');
        }
      } catch { setAddressMode('manual'); }
    };
    fetchAddrs();
  }, []);

  const applyAddress = (addr) => {
    setShippingAddress({
      fullName: addr.fullName || '', street: addr.street || '',
      apartment: addr.apartment || '', city: addr.city || '',
      state: addr.state || '', zip: addr.zip || '',
      country: addr.country || 'US', phone: addr.phone || '',
    });
  };

  const handleSelectAddress = (addr) => {
    setSelectedAddressId(addr._id);
    applyAddress(addr);
  };

  const handleShippingChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const handleBillingChange = (e) => {
    setBillingAddress({ ...billingAddress, [e.target.name]: e.target.value });
  };

  const validateShipping = () => {
    const required = ['fullName', 'street', 'city', 'state', 'zip', 'country'];
    for (const field of required) {
      if (!shippingAddress[field]?.trim()) {
        toast.error(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    return true;
  };

  const handleContinueToReview = () => {
    if (validateShipping()) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      const { data } = await orderService.create({
        shippingAddress,
        billingAddress: sameAsShipping ? shippingAddress : billingAddress,
        paymentMethod, notes,
        shippingMethod: shippingRate?.id,
        shippingCarrier: shippingRate?.carrier,
        shippingCost: shippingRate?.rate || 0,
      });
      if (data.success) {
        navigate('/checkout/payment', {
          state: { orderIds: data.data.map(o => o.id), orders: data.data, summary: data.summary, paymentMethod },
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to place order');
    } finally { setLoading(false); }
  };

  // Save the manual address to address book
  const handleSaveNewAddress = async () => {
    if (!validateShipping()) return;
    try {
      const res = await addressService.create({ ...shippingAddress, label: 'Home', isDefault: savedAddresses.length === 0 });
      toast.success('Address saved to your address book');
      const newAddr = res.data.data;
      setSavedAddresses(prev => [...prev, newAddr]);
      setSelectedAddressId(newAddr._id);
      setAddressMode('saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  if (!cart.items || cart.items.length === 0) return <LoadingSpinner />;

  return (
    <div className="section-padding bg-light" style={{ minHeight: '80vh' }}>
      <div className="container fd-page-container" style={{ maxWidth: '960px' }}>
        {/* Steps */}
        <div className="d-flex align-items-center justify-content-center gap-3 mb-4">
          <div className={`d-flex align-items-center gap-1 ${step >= 1 ? 'fw-semibold' : 'text-muted'}`}
            style={{ color: step >= 1 ? '#B91C1C' : undefined }}>
            <span className="badge rounded-circle" style={{ background: step >= 1 ? '#B91C1C' : '#9CA3AF', color: '#FFF' }}>1</span>
            Shipping
          </div>
          <div className="border-top flex-grow-0" style={{ width: '60px' }}></div>
          <div className={`d-flex align-items-center gap-1 ${step >= 2 ? 'fw-semibold' : 'text-muted'}`}
            style={{ color: step >= 2 ? '#B91C1C' : undefined }}>
            <span className="badge rounded-circle" style={{ background: step >= 2 ? '#B91C1C' : '#9CA3AF', color: '#FFF' }}>2</span>
            Review & Pay
          </div>
        </div>

        <div className="row g-4">
          {/* Main */}
          <div className="col-lg-7">
            {step === 1 && (
              <div className="dashboard-card">
                <h4 className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FiMapPin color="#B91C1C" /> Shipping Address
                </h4>

                {/* Mode toggle */}
                {savedAddresses.length > 0 && (
                  <div className="d-flex gap-2 mb-3">
                    <button className={`btn btn-sm ${addressMode === 'saved' ? 'btn-danger' : 'btn-outline-secondary'}`}
                      onClick={() => setAddressMode('saved')}>
                      <FiMapPin size={13} className="me-1" /> Saved Addresses ({savedAddresses.length})
                    </button>
                    <button className={`btn btn-sm ${addressMode === 'manual' ? 'btn-danger' : 'btn-outline-secondary'}`}
                      onClick={() => setAddressMode('manual')}>
                      <FiEdit2 size={13} className="me-1" /> Enter New
                    </button>
                  </div>
                )}

                {/* Saved addresses mode */}
                {addressMode === 'saved' && savedAddresses.length > 0 && (
                  <AddressBook
                    mode="select"
                    selectedId={selectedAddressId}
                    onSelect={handleSelectAddress}
                    compact
                  />
                )}

                {/* Manual entry mode */}
                {addressMode === 'manual' && (
                  <div>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '13px' }}>{t('fullName')} *</label>
                        <input className="form-control" name="fullName" value={shippingAddress.fullName} onChange={handleShippingChange} required />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '13px' }}>{t('phone')}</label>
                        <input className="form-control" name="phone" value={shippingAddress.phone} onChange={handleShippingChange} placeholder="+1 (555) 000-0000" />
                      </div>
                      <div className="col-12">
                        <label className="form-label" style={{ fontSize: '13px' }}>{t('streetAddress')} *</label>
                        <input className="form-control" name="street" value={shippingAddress.street} onChange={handleShippingChange} required />
                      </div>
                      <div className="col-12">
                        <label className="form-label" style={{ fontSize: '13px' }}>{t('apartment')}</label>
                        <input className="form-control" name="apartment" value={shippingAddress.apartment} onChange={handleShippingChange} placeholder="Optional" />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label" style={{ fontSize: '13px' }}>{t('city')} *</label>
                        <input className="form-control" name="city" value={shippingAddress.city} onChange={handleShippingChange} required />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label" style={{ fontSize: '13px' }}>{t('state')} *</label>
                        <input className="form-control" name="state" value={shippingAddress.state} onChange={handleShippingChange} required />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label" style={{ fontSize: '13px' }}>ZIP *</label>
                        <input className="form-control" name="zip" value={shippingAddress.zip} onChange={handleShippingChange} required />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '13px' }}>{t('country')}</label>
                        <select className="form-select" name="country" value={shippingAddress.country} onChange={handleShippingChange}>
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                          <option value="GB">United Kingdom</option>
                          <option value="IN">India</option>
                          <option value="AU">Australia</option>
                        </select>
                      </div>
                    </div>
                    <button className="btn btn-sm btn-outline-danger mt-3" onClick={handleSaveNewAddress}>
                      <FiPlus size={13} className="me-1" /> Save to Address Book
                    </button>
                  </div>
                )}

                {/* Shipping Rate Selection */}
                <div className="mt-3 mb-3">
                  <ShippingRateSelector
                    cartItems={cart.items}
                    subtotal={subtotal}
                    country={shippingAddress.country}
                    selectedRate={shippingRate}
                    onSelect={setShippingRate}
                  />
                </div>

                {/* Billing same as shipping */}
                <div className="form-check mt-3 mb-3">
                  <input type="checkbox" className="form-check-input" id="sameAsBilling"
                    checked={sameAsShipping}
                    onChange={(e) => {
                      setSameAsShipping(e.target.checked);
                      if (!e.target.checked && !billingAddress) setBillingAddress({ ...shippingAddress });
                    }} />
                  <label className="form-check-label" htmlFor="sameAsBilling">
                    Billing address same as shipping
                  </label>
                </div>

                {!sameAsShipping && billingAddress && (
                  <div className="border-top pt-3">
                    <h5 className="mb-3">{t('billingAddress')}</h5>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <input className="form-control" name="fullName" placeholder="Full Name" value={billingAddress.fullName} onChange={handleBillingChange} />
                      </div>
                      <div className="col-12">
                        <input className="form-control" name="street" placeholder="Street" value={billingAddress.street} onChange={handleBillingChange} />
                      </div>
                      <div className="col-5">
                        <input className="form-control" name="city" placeholder="City" value={billingAddress.city} onChange={handleBillingChange} />
                      </div>
                      <div className="col-4">
                        <input className="form-control" name="state" placeholder="State" value={billingAddress.state} onChange={handleBillingChange} />
                      </div>
                      <div className="col-3">
                        <input className="form-control" name="zip" placeholder="ZIP" value={billingAddress.zip} onChange={handleBillingChange} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-3 mt-3">
                  <label className="form-label">{t('orderNotes')}</label>
                  <textarea className="form-control" rows={2} placeholder="Special delivery instructions..."
                    value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>

                <button className="btn btn-fd-primary btn-lg w-100" onClick={handleContinueToReview}>
                  Continue to Review
                </button>
              </div>
            )}

            {step === 2 && (
              <div>
                {/* Shipping Summary */}
                <div className="dashboard-card mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FiMapPin size={16} color="#B91C1C" /> Shipping To
                    </h5>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setStep(1)}>Edit</button>
                  </div>
                  <p className="mb-0" style={{ fontSize: '14px', lineHeight: 1.6 }}>
                    <strong>{shippingAddress.fullName}</strong><br />
                    {shippingAddress.street}{shippingAddress.apartment ? `, ${shippingAddress.apartment}` : ''}<br />
                    {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}<br />
                    {shippingAddress.country}
                    {shippingAddress.phone && <><br />📞 {shippingAddress.phone}</>}
                  </p>
                </div>

                {/* Payment */}
                <div className="dashboard-card mb-3">
                  <h5 className="mb-3">{t('paymentMethod')}</h5>
                  <div className="d-flex flex-column gap-2">
                    {[
                      { value: 'stripe', label: 'Credit / Debit Card', sub: 'Pay securely with Stripe' },
                      { value: 'razorpay', label: 'Razorpay', sub: 'UPI, Cards, Netbanking, Wallets' },
                    ].map(pm => (
                      <label key={pm.value} className={`border rounded p-3 d-flex align-items-center gap-3`}
                        style={{ cursor: 'pointer', borderColor: paymentMethod === pm.value ? '#B91C1C' : '#E5E7EB',
                          background: paymentMethod === pm.value ? '#FEF2F2' : '#FFF' }}>
                        <input type="radio" name="paymentMethod" value={pm.value}
                          checked={paymentMethod === pm.value}
                          onChange={(e) => setPaymentMethod(e.target.value)} className="form-check-input" />
                        <FiCreditCard size={20} color="#B91C1C" />
                        <div>
                          <strong>{pm.label}</strong>
                          <small className="d-block text-muted">{pm.sub}</small>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Items */}
                <div className="dashboard-card">
                  <h5 className="mb-3">Order Items ({itemCount})</h5>
                  {cart.items.map((item) => (
                    <div key={item.productId} className="d-flex align-items-center gap-3 py-2 border-bottom">
                      <img src={item.image || '/placeholder.png'} alt={item.name}
                        className="rounded" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                      <div className="flex-grow-1">
                        <span className="fw-semibold">{item.name}</span>
                        <small className="d-block text-muted">Qty: {item.quantity}</small>
                      </div>
                      <span className="fw-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="d-flex gap-3 mt-4">
                  <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>
                    <FiChevronLeft className="me-1" /> Back
                  </button>
                  <button className="btn btn-fd-primary btn-lg flex-grow-1" onClick={handlePlaceOrder} disabled={loading}>
                    <FiLock className="me-2" />
                    {loading ? t('placingOrder') : `Place Order — $${total.toFixed(2)}`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="col-lg-5">
            <div className="dashboard-card position-sticky" style={{ top: '100px' }}>
              <h5 className="mb-3">{t('orderSummary')}</h5>
              <div className="d-flex justify-content-between mb-2"><span>Subtotal ({itemCount} items)</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="d-flex justify-content-between mb-2">
                <span>{taxInfo.taxName || 'Tax'} ({(taxInfo.taxRate * 100).toFixed(1)}%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Shipping{shippingRate ? ` (${shippingRate.name})` : ''}</span>
                <span>{shipping === 0 ? <span className="text-success">{t('free')}</span> : `$${shipping.toFixed(2)}`}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between">
                <strong className="fs-5">{t('total')}</strong>
                <strong className="fs-5" style={{ color: '#B91C1C' }}>${total.toFixed(2)}</strong>
              </div>
              <div className="mt-4 p-3 bg-light rounded text-center">
                <FiLock className="text-success me-1" />
                <small className="text-muted">Your payment info is encrypted and secure</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
